import { lazy, ComponentType } from 'react';

/**
 * Wraps React.lazy() to auto-reload on chunk load failures (stale deployments).
 *
 * When Vite deploys a new build, users with old HTML may try to load old chunk hashes
 * that no longer exist on the server (404 → TypeError). This wrapper:
 * 1. Catches the TypeError from failed dynamic import()
 * 2. Reloads the page once (to fetch fresh HTML with new chunk hashes)
 * 3. Guards against infinite reload loops
 *
 * Usage:
 *   const MyPage = lazyWithReload(() => import('./pages/MyPage'));
 *
 * Alternative to manually handling vite:preloadError (which only fires for preloaded chunks).
 */
export function lazyWithReload<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const RELOAD_KEY = 'lazy-reload';
    const MAX_RELOAD_INTERVAL_MS = 10_000; // 10 seconds

    try {
      // Attempt to load the module
      return await importFn();
    } catch (error) {
      // Only handle chunk load errors (TypeError from failed import())
      if (!(error instanceof TypeError && error.message.includes('dynamically imported module'))) {
        throw error; // Re-throw other errors (network, syntax, etc.)
      }

      // Check if we've already reloaded recently (prevent infinite loops)
      const lastReload = sessionStorage.getItem(RELOAD_KEY);
      const now = Date.now();

      if (!lastReload || now - Number(lastReload) > MAX_RELOAD_INTERVAL_MS) {
        // Mark reload timestamp and reload
        sessionStorage.setItem(RELOAD_KEY, String(now));
        window.location.reload();

        // Return a never-resolving promise to prevent rendering during reload
        return new Promise(() => {});
      }

      // Already reloaded recently — let error boundary handle it
      throw error;
    }
  });
}
